import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AIDLC_SUITE_BUNDLES,
  WORKSPACE_BILLABLE_PRODUCTS,
  type ProductId,
} from '@/config/products';
import { PRODUCT_BILLING } from '@/config/billingCatalog';
import {
  WORKSPACE_BILLABLE_PRODUCT_IDS,
  estimateWorkspaceMonthlyUsd,
  type WorkspaceBillableProductId,
} from '@/config/pricing';
import { PLAN_TRIAL_DAYS } from '@/config/planFeatures';
import ProductTagChip from '@/components/products/ProductTagChip';
import {
  useCreateAidlcCheckoutMutation,
} from '@/store/api/apiSlice';
import { FiCheck, FiChevronDown, FiChevronUp, FiExternalLink } from 'react-icons/fi';
import { toast } from 'react-toastify';
import type { TenantProductAccess } from '@/utils/productAccess';
import { getApiError } from '@/utils/errorUtils';
import { startBillingCheckout } from '@/utils/razorpayCheckout';

type ProductBillingMap = Partial<Record<ProductId, TenantProductAccess>>;

const DEFAULT_SELECTION = Object.fromEntries(
  WORKSPACE_BILLABLE_PRODUCT_IDS.map((id) => [id, id === 'pm']),
) as Record<WorkspaceBillableProductId, boolean>;

type Props = {
  subscriptionProducts?: ProductBillingMap | null;
  billableSeatsByProduct?: Partial<Record<ProductId, number>>;
  hasPaidSubscription?: boolean;
  billingConfigured?: boolean;
  billingProvider?: string;
  onManageSubscription?: () => void;
  portalLoading?: boolean;
};

function productTrialBadge(entry?: TenantProductAccess) {
  if (!entry?.enabled) return null;
  if (entry.status === 'active') return <span className="text-[9px] font-bold text-emerald-700">Paid</span>;
  if (entry.status === 'expired' || entry.needsPayment)
    return <span className="text-[9px] font-bold text-amber-700">Trial done</span>;
  if (entry.status === 'trial' && entry.daysLeft != null)
    return <span className="text-[9px] font-bold text-indigo-600">{entry.daysLeft}d trial</span>;
  if (entry.status === 'trial')
    return <span className="text-[9px] font-bold text-indigo-600">{PLAN_TRIAL_DAYS}d trial</span>;
  return null;
}

export default function AidlcBillingMatrix({
  subscriptionProducts,
  billableSeatsByProduct,
  hasPaidSubscription,
  billingConfigured = false,
  billingProvider = 'none',
  onManageSubscription,
  portalLoading,
}: Props) {
  const checkoutEnabled = billingConfigured;
  const seatsByProduct = useMemo(
    () => ({
      pm: Math.max(1, billableSeatsByProduct?.pm ?? 1),
      code: billableSeatsByProduct?.code ?? 0,
      ship: billableSeatsByProduct?.ship ?? 0,
      lenc: billableSeatsByProduct?.lenc ?? 0,
    }),
    [billableSeatsByProduct],
  );

  const [selected, setSelected] = useState<Record<WorkspaceBillableProductId, boolean>>({
    ...DEFAULT_SELECTION,
  });
  const [showBundles, setShowBundles] = useState(false);
  const [createAidlcCheckout, { isLoading }] = useCreateAidlcCheckoutMutation();

  const addOnProducts = useMemo(
    () => WORKSPACE_BILLABLE_PRODUCTS.filter((p) => p.id !== 'pm'),
    [],
  );

  const enabledProducts = useMemo(
    () => WORKSPACE_BILLABLE_PRODUCTS.filter((p) => selected[p.id as WorkspaceBillableProductId]),
    [selected],
  );

  const estimatedMonthly = useMemo(() => {
    const ids = enabledProducts.map((p) => p.id as WorkspaceBillableProductId);
    return estimateWorkspaceMonthlyUsd(ids, seatsByProduct);
  }, [enabledProducts, seatsByProduct]);

  const toggle = (id: WorkspaceBillableProductId) => {
    if (hasPaidSubscription) return;
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSubscribe = async () => {
    if (!checkoutEnabled) {
      toast.info('Online billing is not configured for this environment.');
      return;
    }
    const products = WORKSPACE_BILLABLE_PRODUCTS.filter(
      (p) => selected[p.id as WorkspaceBillableProductId],
    ).map((p) => ({
      productId: p.id,
      enabled: true,
    }));

    try {
      const result = await createAidlcCheckout({
        interval: 'monthly',
        products,
      }).unwrap();
      await startBillingCheckout(result);
    } catch (e) {
      const message = getApiError(e, 'Checkout failed. Try again or contact support.');
      if (message !== 'Checkout cancelled') toast.error(message);
    }
  };

  const pmEntry = subscriptionProducts?.pm;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
        <h2 className="text-sm font-semibold text-slate-800">Suite products</h2>
        <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">
          CoTaskr Plan bills all active members ({seatsByProduct.pm}). Code and Ship bill only assigned
          users — manage assignments in{' '}
          <Link to="/admin/org-settings?tab=product-access" className="text-indigo-600 hover:underline">
            Org Settings → Product Access
          </Link>
          .
        </p>

        {hasPaidSubscription && (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-[11px] text-emerald-900">
            Active subscription — seat counts sync automatically. Use billing settings to update payment
            method or cancel.
          </div>
        )}

        <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <ProductTagChip productId="pm" size="sm" />
              <p className="mt-2 text-[13px] font-bold text-slate-900">CoTaskr Plan</p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Delivery, time tracking, economics — ${PRODUCT_BILLING.pm.priceUsd}/active user/mo
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg font-extrabold text-slate-900">
                ${seatsByProduct.pm * PRODUCT_BILLING.pm.priceUsd}
                <span className="text-[11px] font-medium text-slate-500">/mo</span>
              </p>
              <p className="text-[10px] text-slate-500">
                {seatsByProduct.pm} users on Plan × ${PRODUCT_BILLING.pm.priceUsd}
              </p>
              {pmEntry && <div className="mt-1">{productTrialBadge(pmEntry)}</div>}
            </div>
          </div>
        </div>

        {addOnProducts.length > 0 && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {addOnProducts.map((product) => {
              const pricing = PRODUCT_BILLING[product.id];
              const id = product.id as WorkspaceBillableProductId;
              const on = hasPaidSubscription
                ? !!subscriptionProducts?.[product.id]?.enabled
                : selected[id];
              const subEntry = subscriptionProducts?.[product.id];
              const seatBased = pricing.billingModel !== 'per_org_flat';
              const productSeats =
                product.id === 'code'
                  ? seatsByProduct.code
                  : product.id === 'ship'
                    ? seatsByProduct.ship
                    : seatsByProduct.lenc;

              return (
                <div
                  key={product.id}
                  className={`rounded-xl border p-4 transition-all ${
                    on ? 'border-indigo-300 bg-indigo-50/40' : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <ProductTagChip productId={product.id} size="sm" />
                    {!hasPaidSubscription && (
                      <label className="flex items-center gap-2 text-[11px] font-semibold text-slate-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggle(id)}
                          className="rounded border-slate-300 text-indigo-600"
                        />
                        {on ? 'On' : 'Off'}
                      </label>
                    )}
                    {hasPaidSubscription && subEntry?.enabled && (
                      <span className="text-[10px] font-semibold text-emerald-700">Enabled</span>
                    )}
                  </div>
                  <p className="mt-2 text-[13px] font-bold text-slate-900">{product.name}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{pricing.shortLabel}</p>
                  <p className="text-lg font-extrabold text-slate-900 mt-1">
                    ${seatBased ? productSeats * pricing.priceUsd : pricing.priceUsd}
                    <span className="text-[11px] font-medium text-slate-500">
                      {seatBased ? '/mo' : pricing.unitLabel}
                    </span>
                  </p>
                  {seatBased && (
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {productSeats} assigned user{productSeats === 1 ? '' : 's'} × ${pricing.priceUsd}
                    </p>
                  )}
                  {subEntry && <div className="mt-1">{productTrialBadge(subEntry)}</div>}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <div>
            <p className="text-[11px] text-slate-500">Estimated monthly (assigned users per product)</p>
            <p className="text-xl font-bold text-slate-900">${estimatedMonthly}</p>
          </div>
          {hasPaidSubscription ? (
            <button
              type="button"
              onClick={onManageSubscription}
              disabled={portalLoading || !checkoutEnabled}
              className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-60"
            >
              <FiExternalLink className="w-4 h-4" />
              {portalLoading ? 'Opening…' : billingProvider === 'razorpay' ? 'Billing help' : 'Manage billing'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void handleSubscribe()}
              disabled={isLoading || !checkoutEnabled}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {isLoading ? 'Opening checkout…' : `Start subscription (${PLAN_TRIAL_DAYS}-day trial)`}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => setShowBundles((v) => !v)}
          className="w-full flex items-center justify-between gap-2 px-5 py-3 text-left hover:bg-slate-50 transition-colors"
        >
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Reference bundles</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Typical combinations — optional reading</p>
          </div>
          {showBundles ? (
            <FiChevronUp className="w-4 h-4 text-slate-400 shrink-0" />
          ) : (
            <FiChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
          )}
        </button>
        {showBundles && (
          <div className="px-5 pb-5 border-t border-slate-100">
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {AIDLC_SUITE_BUNDLES.map((bundle) => (
                <div key={bundle.id} className="rounded-lg border border-slate-100 px-3 py-2">
                  <p className="text-[12px] font-bold text-slate-800">{bundle.name}</p>
                  <p className="text-[11px] text-slate-500">{bundle.priceHint}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {bundle.products.map((pid) => (
                      <ProductTagChip key={pid} productId={pid} size="sm" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-slate-500 flex items-start gap-1">
              <FiCheck className="shrink-0 mt-0.5 text-emerald-600" />
              Project Economics is included with CoTaskr Plan — not a separate product.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
